import { Button, Paper, styled, TextField as MuiTextField, Select as MuiSelect } from "@mui/material";

import { convertHexToRGBA } from "utils/commonUtils";
import { getInvertColor } from "utils/themeUtils";

import authBackground from "assets/img/BoqMedewerkerRaketDameGebouw.png";
import shape1 from "./shape-1.svg";
import shape2 from "./shape-2.svg";

const adminLoginSVG = "https://riddlestory-bucket1.s3.eu-central-1.amazonaws.com/assets/defaultBackground.png";

export const AdminLoginScreenContainer = styled("div")({
	display: "block",
	justifyContent: "center",
	alignItems: "center",
	height: "100%",
	width: "100%",
	background: `no-repeat center center fixed url(${adminLoginSVG})`,
	backgroundSize: "cover",
	position: "relative",
	overflowY: "scroll",
});

export const LoginContainer = styled("div")(({ theme }) => ({
	maxWidth: 572,
	minWidth: 400,
	width: "100%",
	height: "100%",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	[theme.breakpoints.up("desktop")]: {
		maxHeight: `calc(100vh - 15%)`,
	},
	[theme.breakpoints.down("mobile")]: {
		transform: "scale(100%)",
		overflowY: "scroll",
		minWidth: 0,
		justifyContent: "flex-end",
		backgroundColor: convertHexToRGBA("fff", 1),
		paddingBottom: 16,
	},
}));

export const LoginCard = styled(Paper)(({ theme }) => ({
	borderRadius: 5,
	padding: "3.25rem",
	backgroundColor: convertHexToRGBA("fff", 1),
	height: "fit-content",
	[theme.breakpoints.down("mobile")]: {
		padding: "50px 16px",
		height: "inherit",
		paddingTop: 0,
		paddingBottom: 0,
	},
}));

export const TextField = styled(MuiTextField)(({ theme }) => ({
	fontSize: "16px",
	fontStyle: "normal",
	fontWeight: 400,
	lineHeight: "normal",
	"& .MuiInputLabel-root": {
		top: "6px",
		left: "4px",
	},
	"&:has(.Mui-error)": {
		"& .MuiFilledInput-root": {
			border: `2px solid ${theme.palette.error.main}`,
		},
	},
	"& .MuiFilledInput-root": {
		padding: "0 16px",
		height: "64px",
		borderRadius: "6px",
		background: "#F4F4F4",
		"& input": {
			paddingTop: "20px",
			paddingLeft: "0px",
			paddingRight: "0px",
		},
		"&:before": {
			border: "none",
			height: "100%",
			width: "100%",
			borderRadius: "6px",
		},
		"&:after": {
			border: "none",
			height: "100%",
			width: "100%",
			borderRadius: "6px",
		},
		"&:hover": {
			background: "#F4F4F4",
			"&:before": {
				borderBottom: "none",
			},
		},
		"&:has(input:focus)": {
			border: `2px solid ${theme.palette.primary.main}`,
		},
	},
}));

export const Select = styled(MuiSelect)(({ theme }) => ({
	borderRadius: "6px",
	background: "#F4F4F4",
	height: "64px",
	overflow: "hidden",
	"& .MuiFilledInput-input": {
		height: "auto",
		padding: "26px 16px 14px 16px",
	},
	"& input": {
		paddingTop: "20px",
		paddingLeft: "0px",
		paddingRight: "0px",
	},
	"&:before": {
		border: "none",
		height: "100%",
		width: "100%",
		borderRadius: "6px",
	},
	"&:after": {
		border: "none",
		height: "100%",
		width: "100%",
		borderRadius: "6px",
	},
	"&:hover": {
		background: "#F4F4F4",
		"&:before": {
			borderBottom: "none",
		},
	},
	"&:has(input:focus)": {
		border: `2px solid ${theme.palette.primary.main}`,
	},
}));

export const AdminLoginCard = styled(Paper)(({ theme }) => ({
	borderRadius: 5,
	padding: 0,
	backgroundColor: convertHexToRGBA("fff", 1),
	height: "fit-content",
	border: "none",
	boxShadow: "none",
	maxWidth: "530px",
	width: "100%",
	[theme.breakpoints.down("mobile")]: {
		padding: "50px 16px",
		height: "inherit",
		paddingTop: 0,
		paddingBottom: 0,
	},
}));

export const WelcomeTextContainer = styled("div")(({ theme }) => ({
	background: `linear-gradient(0deg, ${convertHexToRGBA(theme.palette.primary.main, 0.9)} 0%, ${convertHexToRGBA(
		theme.palette.primary.main,
		0.9,
	)} 100%), url(${authBackground}), lightgray 50% / cover no-repeat`,
	backgroundSize: "100% auto",
	backgroundPosition: "center",
	display: "flex",
	height: "100%",
	alignItems: "center",
	justifyContent: "center",
	padding: "0 94px",
	position: "relative",
	":before": {
		content: '""',
		position: "absolute",
		top: "100px",
		right: "100px",
		width: "120px",
		height: "120px",
		background: `url(${shape1}) no-repeat center center`,
	},
	":after": {
		content: '""',
		position: "absolute",
		bottom: "0",
		right: "0",
		width: "100%",
		height: "100%",
		background: `url(${shape2}) no-repeat bottom left`,
	},
}));

export const WelcomeText = styled("p")(({ theme }) => ({
	color: "#FFF",
	fontSize: "22px",
	fontStyle: "normal",
	fontWeight: 400,
	lineHeight: "1.9rem",
	position: "relative",
	whiteSpace: "pre-line",
	":before": {
		color: theme.palette.secondary.main,
		fontFamily: "Gayathri",
		fontWeight: 700,
		fontSize: "96px",
		lineHeight: "normal",
		content: '"“"',
		position: "absolute",
		top: "-80px",
		left: "0",
	},
	":after": {
		position: "absolute",
		bottom: "-68px",
		right: "0",
		width: "36px",
		height: "36px",
		borderRight: "12px solid #FFF",
		borderBottom: "12px solid #FFF",
		content: '""',
	},
}));

export const InputGroup = styled("div")({
	marginBottom: 16,
	"&.row": {
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		gap: 16,
		"& .MuiTypography-root": {
			whiteSpace: "nowrap",
		},
	},
	"&.steps": {
		transition: "all 0.5s",
		flex: 1,
		"&.hidden": {
			opacity: "0",
			maxHeight: 0,
		},
		"&.visible": {
			opacity: "1",
			maxHeight: "1000px",
		},
	},
});

interface LoginButtonProps extends React.ComponentProps<typeof Button> {
	isHidden?: boolean;
}

export const LoginButton = styled(Button)<LoginButtonProps>(({ theme, isHidden }) => ({
	color: getInvertColor(theme.palette.primary.main),
	borderRadius: "6px",
	height: "56px",
	fontSize: "20px",
	fontWeight: 800,
	textTransform: "capitalize",
	opacity: isHidden ? 0 : 1,
	transition: "all 0.2s ease-in",
}));

export const BackButton = styled(Button)(({ theme }) => ({
	[theme.breakpoints.down("mobile")]: {
		position: "absolute",
		top: 5,
		left: -10,
		boxShadow: "none",
		backgroundColor: "transparent",
		color: "white",
		textTransform: "none",
		zIndex: 4,
		"& > *": { color: "white" },
	},
}));

export const HeaderWithSubText = styled("div")(({ theme }) => ({
	fontWeight: "bold",
	marginBottom: "32px",

	[theme.breakpoints.down("mobile")]: {
		marginTop: "0rem",
	},
}));

export const HeaderWithSubTextIcon = styled("div")(({ theme }) => ({
	fontWeight: "bold",
	marginTop: "1rem",

	marginBottom: "3rem",
	textAlign: "center",
	[theme.breakpoints.down("mobile")]: {
		marginTop: "0rem",
		marginBottom: "1rem",
	},
}));

export const SigInWrapper = styled("div")(({ theme }) => ({
	textAlign: "left",
	marginBottom: "3rem",
	[theme.breakpoints.down("mobile")]: {
		marginBottom: "0.875rem",
	},
}));

export const Banner = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	alignItems: "center",
	overflow: "hidden",

	padding: "20px",
	height: "96px",
	borderRadius: "6px",
}));
